import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const AlternativeModal = ({ isOpen, onClose, suggestion, onAlternativeGenerated, alternativeCount, setAlternativeCount }) => {
  const { t, i18n } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState(0);

  const generationMessages = [
    t('dashboard.alternative.generating1'),
    t('dashboard.alternative.generating2'),
    t('dashboard.alternative.generating3'),
    t('dashboard.alternative.generating4')
  ];

  const handleGetAlternative = async () => {
    setIsGenerating(true);
    setGenerationStep(0);

    // Animate through generation steps
    const stepInterval = setInterval(() => {
      setGenerationStep(prev => {
        if (prev < generationMessages.length - 1) {
          return prev + 1;
        } else {
          clearInterval(stepInterval);
          return prev;
        }
      });
    }, 1500);

    try {
      const response = await axios.post(
        `${API}/suggestion/alternative`,
        {
          suggestion_id: suggestion.suggestion_id,
          alternative_count: alternativeCount + 1
        }
      );

      clearInterval(stepInterval);
      onAlternativeGenerated(response.data);
      setAlternativeCount(prev => prev + 1);
      onClose();

      toast.success(t('dashboard.alternative.success'), { duration: 3000 });
    } catch (e) {
      clearInterval(stepInterval);
      console.error('[AlternativeModal] alternative suggestion error:', e.response?.data || e);
      toast.error(t('dashboard.alternative.error'));
      setIsGenerating(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="alt-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center"
          onClick={onClose}
        >
          {/* Modal - Bottom sheet style */}
          <motion.div
            key="alt-modal-card"
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="bg-white dark:bg-[#18181C] w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl overflow-hidden sm:mx-4 sm:mb-0 min-h-[420px]"
            onClick={(e) => e.stopPropagation()}
            style={{ fontFamily: 'Manrope-Variable, sans-serif' }}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-stone-200 dark:bg-[#2E2E36]" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-2 pb-4">
              <span
                className="text-[10px] tracking-[0.2em] uppercase text-stone-400 dark:text-[#606060]"
                style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace' }}
              >
                {t('dashboard.alternative.title') || (i18n.language === 'it' ? 'Alternativa' : i18n.language === 'es' ? 'Alternativa' : 'Alternative')}
              </span>
              <button
                onClick={onClose}
                className="w-6 h-6 flex items-center justify-center text-stone-400 hover:text-stone-600 dark:text-[#606060] dark:hover:text-[#909090] transition-colors"
              >
                <Icon icon="solar:close-circle-linear" width={18} />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 pb-10 pt-2">
              {!isGenerating ? (
                <>
                  {/* Question */}
                  <div className="text-center mb-10 pt-6">
                    <h3 className="text-lg font-medium text-stone-900 dark:text-[#EFEFEF] mb-2" style={{ fontFamily: 'Manrope-Variable' }}>
                      {t('dashboard.alternative.question') || (i18n.language === 'it' ? 'Ti serve qualcosa di diverso?' : i18n.language === 'es' ? '¿Necesitas algo diferente?' : 'Need something different?')}
                    </h3>
                    <p className="text-sm text-stone-500 dark:text-[#808080]" style={{ fontFamily: 'Manrope-Variable' }}>
                      {t('dashboard.alternative.description') || (i18n.language === 'it' ? 'Posso suggerirti un\'altra idea personalizzata per oggi.' : i18n.language === 'es' ? 'Puedo sugerirte otra idea personalizada para hoy.' : 'I can suggest another personalized idea for today.')}
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="space-y-4">
                    <button
                      onClick={onClose}
                      className="w-full h-12 bg-stone-900 dark:bg-[#EFEFEF] text-white dark:text-[#18181C] rounded-full font-medium hover:bg-stone-800 dark:hover:bg-white transition-colors text-sm tracking-wide"
                      style={{ fontFamily: 'Manrope-Variable' }}
                    >
                      {t('dashboard.alternative.noThanks') || (i18n.language === 'it' ? 'No, grazie' : i18n.language === 'es' ? 'No, gracias' : 'No, thanks')}
                    </button>

                    <button
                      onClick={handleGetAlternative}
                      className="w-full py-3 text-stone-500 dark:text-[#808080] hover:text-stone-700 dark:hover:text-[#EFEFEF] transition-colors text-sm"
                      style={{ fontFamily: 'Manrope-Variable' }}
                    >
                      {t('dashboard.alternative.seeAlternative') || (i18n.language === 'it' ? 'Vedi un\'alternativa' : i18n.language === 'es' ? 'Ver una alternativa' : 'See an alternative')}
                    </button>
                  </div>
                </>
              ) : (
                /* Loading State */
                <div className="text-center py-12">
                  <div className="w-12 h-12 mx-auto mb-6 rounded-full bg-stone-900 dark:bg-[#EFEFEF] animate-pulse" />

                  <p className="text-sm text-stone-500 dark:text-[#808080] animate-pulse" style={{ fontFamily: 'Manrope-Variable' }}>
                    {generationMessages[generationStep]}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AlternativeModal;